import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClerk } from "@clerk/tanstack-react-start";
import type {
  SignInFirstFactor,
  SignInResource,
  SignInSecondFactor,
} from "@clerk/types";
import { getClerkErrorMessage } from "~/lib/clerk-errors";

export type SupportedFirstFactorStrategy = Extract<
  SignInFirstFactor["strategy"],
  "password" | "email_code" | "email_link"
>;

export type SupportedSecondFactorStrategy = Extract<
  SignInSecondFactor["strategy"],
  "totp" | "backup_code"
>;

interface EmailCodeState {
  lastSentAt: number;
  emailAddressId: string;
}

interface EmailLinkState {
  lastSentAt: number;
  emailLinkUrl: string | null;
}

interface UseSignInControllerOptions {
  onSignInComplete?: () => Promise<void> | void;
}

interface SignInControllerState {
  stage:
    | "idle"
    | "submitting"
    | "awaiting_email_code"
    | "awaiting_second_factor"
    | "awaiting_email_link";
  identifier: string | null;
  error: string | null;
  availableFirstFactors: SupportedFirstFactorStrategy[];
  availableSecondFactors: SupportedSecondFactorStrategy[];
  requiredSecondFactor: SupportedSecondFactorStrategy | null;
  emailCode: EmailCodeState | null;
  emailLink: EmailLinkState | null;
}

const INITIAL_STATE: SignInControllerState = {
  stage: "idle",
  identifier: null,
  error: null,
  availableFirstFactors: [],
  availableSecondFactors: [],
  requiredSecondFactor: null,
  emailCode: null,
  emailLink: null,
};

type EmailCodeFactor = Extract<SignInFirstFactor, { strategy: "email_code" }>;
type EmailLinkFactor = Extract<SignInFirstFactor, { strategy: "email_link" }>;

function toSupportedFirstFactorStrategy(
  factor: SignInFirstFactor,
): SupportedFirstFactorStrategy | null {
  if (
    factor.strategy === "password" ||
    factor.strategy === "email_code" ||
    factor.strategy === "email_link"
  ) {
    return factor.strategy;
  }
  return null;
}

function toSupportedSecondFactorStrategy(
  factor: SignInSecondFactor,
): SupportedSecondFactorStrategy | null {
  if (factor.strategy === "totp" || factor.strategy === "backup_code") {
    return factor.strategy;
  }
  return null;
}

function findEmailCodeFactor(signIn: SignInResource): EmailCodeFactor | null {
  return (
    signIn.supportedFirstFactors?.find(
      (factor): factor is EmailCodeFactor =>
        Boolean(factor) && factor.strategy === "email_code",
    ) ?? null
  );
}

function findEmailLinkFactor(signIn: SignInResource): EmailLinkFactor | null {
  return (
    signIn.supportedFirstFactors?.find(
      (factor): factor is EmailLinkFactor =>
        Boolean(factor) && factor.strategy === "email_link",
    ) ?? null
  );
}

export interface SignInController {
  readonly state: SignInControllerState;
  readonly availableFirstFactors: SupportedFirstFactorStrategy[];
  readonly availableSecondFactors: SupportedSecondFactorStrategy[];
  startPasswordSignIn: (params: {
    identifier: string;
    password: string;
  }) => Promise<void>;
  startEmailCodeSignIn: (params: { identifier: string }) => Promise<void>;
  resendEmailCode: () => Promise<void>;
  verifyEmailCode: (params: { code: string }) => Promise<void>;
  startEmailLinkSignIn: (params: {
    identifier: string;
    redirectUrl: string;
  }) => Promise<void>;
  verifySecondFactor: (params: {
    strategy: SupportedSecondFactorStrategy;
    code: string;
  }) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export function useSignInController(
  options: UseSignInControllerOptions = {},
): SignInController {
  const clerk = useClerk();
  const [state, setState] = useState<SignInControllerState>(INITIAL_STATE);
  const signInRef = useRef<SignInResource | null>(null);

  const ensureClerkClient = useCallback(() => {
    if (!clerk || !clerk.client) {
      throw new Error("Authentication service unavailable");
    }
    return clerk.client;
  }, [clerk]);

  const reset = useCallback(() => {
    signInRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const updateAvailableFactors = useCallback(
    (signIn: SignInResource | null) => {
      if (!signIn) {
        setState((prev) => ({
          ...prev,
          availableFirstFactors: [],
          availableSecondFactors: [],
        }));
        return;
      }

      const firstFactors =
        signIn.supportedFirstFactors
          ?.map(toSupportedFirstFactorStrategy)
          .filter(
            (factor): factor is SupportedFirstFactorStrategy => factor !== null,
          ) ?? [];
      const secondFactors =
        signIn.supportedSecondFactors
          ?.map(toSupportedSecondFactorStrategy)
          .filter(
            (factor): factor is SupportedSecondFactorStrategy =>
              factor !== null,
          ) ?? [];

      setState((prev) => ({
        ...prev,
        availableFirstFactors: firstFactors,
        availableSecondFactors: secondFactors,
      }));
    },
    [],
  );

  const handleSignInCompletion = useCallback(
    async (signIn: SignInResource): Promise<void> => {
      updateAvailableFactors(signIn);
      switch (signIn.status) {
        case "complete": {
          if (!signIn.createdSessionId) {
            throw new Error("Sign-in completed without session ID");
          }
          await clerk.setActive({ session: signIn.createdSessionId });
          await options.onSignInComplete?.();
          reset();
          return;
        }
        case "needs_second_factor": {
          setState((prev) => ({
            ...prev,
            stage: "awaiting_second_factor",
            error: null,
            requiredSecondFactor: toSupportedSecondFactorStrategy(
              signIn.firstFactorVerification as SignInSecondFactor,
            ),
          }));
          return;
        }
        case "needs_first_factor": {
          setState((prev) => ({
            ...prev,
            stage: "awaiting_email_code",
            error: null,
          }));
          return;
        }
        case "needs_identifier": {
          setState((prev) => ({
            ...prev,
            stage: "idle",
            error: null,
          }));
          return;
        }
        default: {
          return;
        }
      }
    },
    [clerk, options.onSignInComplete, reset, updateAvailableFactors],
  );

  const startPasswordSignIn = useCallback<
    SignInController["startPasswordSignIn"]
  >(
    async ({ identifier, password }) => {
      const client = ensureClerkClient();
      setState((prev) => ({
        ...prev,
        stage: "submitting",
        identifier,
        error: null,
        emailCode: null,
        emailLink: null,
      }));
      try {
        const signIn = await client.signIn.create({ identifier, password });
        signInRef.current = signIn;
        await handleSignInCompletion(signIn);
      } catch (error) {
        const message = getClerkErrorMessage(
          error,
          "Unable to sign in with password.",
        );
        setState((prev) => ({
          ...prev,
          stage: "idle",
          identifier,
          error: message,
        }));
        throw error;
      }
    },
    [ensureClerkClient, handleSignInCompletion],
  );

  const startEmailCodeSignIn = useCallback<
    SignInController["startEmailCodeSignIn"]
  >(
    async ({ identifier }) => {
      const client = ensureClerkClient();
      setState((prev) => ({
        ...prev,
        stage: "submitting",
        identifier,
        error: null,
        emailLink: null,
      }));
      try {
        const initialAttempt = await client.signIn.create({ identifier });
        signInRef.current = initialAttempt;
        updateAvailableFactors(initialAttempt);

        const emailCodeFactor = findEmailCodeFactor(initialAttempt);
        if (!emailCodeFactor) {
          throw new Error("Email code authentication is not available.");
        }

        const preparedAttempt = await initialAttempt.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailCodeFactor.emailAddressId,
        });
        signInRef.current = preparedAttempt;
        updateAvailableFactors(preparedAttempt);
        setState((prev) => ({
          ...prev,
          stage: "awaiting_email_code",
          identifier,
          emailCode: {
            lastSentAt: Date.now(),
            emailAddressId: emailCodeFactor.emailAddressId,
          },
          error: null,
        }));
      } catch (error) {
        const message = getClerkErrorMessage(
          error,
          "Failed to send the verification code. Please try again.",
        );
        setState((prev) => ({
          ...prev,
          stage: "idle",
          identifier,
          error: message,
        }));
        throw error;
      }
    },
    [ensureClerkClient, handleSignInCompletion, updateAvailableFactors],
  );

  const resendEmailCode = useCallback(async () => {
    const signIn = signInRef.current;
    if (!signIn) {
      throw new Error("No sign-in attempt available");
    }
    const emailFactor = findEmailCodeFactor(signIn);
    if (!emailFactor) {
      throw new Error("Email code authentication is not available.");
    }
    try {
      const updated = await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailFactor.emailAddressId,
      });
      signInRef.current = updated;
      updateAvailableFactors(updated);
      setState((prev) => ({
        ...prev,
        stage: "awaiting_email_code",
        emailCode: {
          lastSentAt: Date.now(),
          emailAddressId: emailFactor.emailAddressId,
        },
        error: null,
      }));
    } catch (error) {
      const message = getClerkErrorMessage(
        error,
        "Failed to resend the verification code.",
      );
      setState((prev) => ({ ...prev, error: message }));
      throw error;
    }
  }, [updateAvailableFactors]);

  const verifyEmailCode = useCallback<SignInController["verifyEmailCode"]>(
    async ({ code }) => {
      const signIn = signInRef.current;
      if (!signIn) {
        throw new Error("No verification attempt in progress");
      }
      setState((prev) => ({
        ...prev,
        stage: "submitting",
        error: null,
      }));
      try {
        const verified = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code,
        });
        signInRef.current = verified;
        await handleSignInCompletion(verified);
      } catch (error) {
        const message = getClerkErrorMessage(
          error,
          "Invalid or expired verification code.",
        );
        setState((prev) => ({
          ...prev,
          stage: "awaiting_email_code",
          error: message,
        }));
        throw error;
      }
    },
    [handleSignInCompletion],
  );

  const startEmailLinkSignIn = useCallback<
    SignInController["startEmailLinkSignIn"]
  >(
    async ({ identifier, redirectUrl }) => {
      const client = ensureClerkClient();
      setState((prev) => ({
        ...prev,
        stage: "submitting",
        identifier,
        error: null,
        emailCode: null,
      }));
      try {
        const initialAttempt = await client.signIn.create({ identifier });
        signInRef.current = initialAttempt;
        updateAvailableFactors(initialAttempt);
        const emailLinkFactor = findEmailLinkFactor(initialAttempt);
        if (!emailLinkFactor) {
          throw new Error("Magic link authentication is not available.");
        }

        const emailLinkFlow = await initialAttempt.createEmailLinkFlow();
        const flowResult = await emailLinkFlow.startEmailLinkFlow({
          redirectUrl,
          emailAddressId: emailLinkFactor.emailAddressId,
        });
        signInRef.current = flowResult;
        updateAvailableFactors(flowResult);
        if (flowResult.status === "complete") {
          await handleSignInCompletion(flowResult);
          return;
        }
        setState((prev) => ({
          ...prev,
          stage: "awaiting_email_link",
          emailLink: {
            lastSentAt: Date.now(),
            emailLinkUrl: null,
          },
          error: null,
        }));
      } catch (error) {
        const message = getClerkErrorMessage(
          error,
          "Failed to send the magic link. Please try again.",
        );
        setState((prev) => ({
          ...prev,
          stage: "idle",
          identifier,
          error: message,
        }));
        throw error;
      }
    },
    [ensureClerkClient, handleSignInCompletion, updateAvailableFactors],
  );

  const verifySecondFactor = useCallback<
    SignInController["verifySecondFactor"]
  >(
    async ({ strategy, code }) => {
      const signIn = signInRef.current;
      if (!signIn) {
        throw new Error("No sign-in attempt available");
      }
      setState((prev) => ({
        ...prev,
        stage: "submitting",
        error: null,
      }));
      try {
        const verified = await signIn.attemptSecondFactor({ strategy, code });
        signInRef.current = verified;
        await handleSignInCompletion(verified);
      } catch (error) {
        const message = getClerkErrorMessage(
          error,
          "Failed to verify the second factor. Please try again.",
        );
        setState((prev) => ({
          ...prev,
          stage: "awaiting_second_factor",
          error: message,
        }));
        throw error;
      }
    },
    [handleSignInCompletion],
  );

  useEffect(() => {
    if (state.stage !== "awaiting_email_link") {
      return;
    }
    if (clerk.isSignedIn) {
      void (async () => {
        try {
          await options.onSignInComplete?.();
        } finally {
          reset();
        }
      })();
    }
  }, [clerk.isSignedIn, options.onSignInComplete, reset, state.stage]);

  const availableFirstFactors = useMemo(
    () => state.availableFirstFactors,
    [state.availableFirstFactors],
  );

  const availableSecondFactors = useMemo(
    () => state.availableSecondFactors,
    [state.availableSecondFactors],
  );

  return {
    state,
    availableFirstFactors,
    availableSecondFactors,
    startPasswordSignIn,
    startEmailCodeSignIn,
    resendEmailCode,
    verifyEmailCode,
    startEmailLinkSignIn,
    verifySecondFactor,
    clearError,
    reset,
  } satisfies SignInController;
}
